/**
 * MuiCustomersList - Optimized Customers List Page
 *
 * OPTIMIZATION NOTES (Phase 2.1):
 * - Uses server-side pagination via usePaginatedData hook
 * - Uses server-side search with 300ms debounce
 * - Category filter still client-side (fetched once for chips)
 * - Analytics computed from paginated data for visible subset
 * - Removed Redux dependency - direct API call
 *
 * @see OPTIMIZATION_PLAN.md for full details
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Grid,
  Avatar,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import SearchIcon from "@mui/icons-material/Search"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"
import PeopleIcon from "@mui/icons-material/People"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import CategoryIcon from "@mui/icons-material/Category"
import HomeIcon from "@mui/icons-material/Home"
import BarChartIcon from "@mui/icons-material/BarChart"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import ClearIcon from "@mui/icons-material/Clear"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"

// Optimized API imports
import { getCustomersPaginated, fetchPaginated } from "helpers/api_helper"
import { GET_CUSTOMERS } from "helpers/api_urls"
import usePaginatedData from "hooks/usePaginatedData"

/**
 * Fetch category counts for filter chips (lightweight - just distinct categories)
 */
async function fetchCategoryCounts() {
  try {
    // Fetch all customers but only for category extraction
    // In a future optimization, this could be a dedicated endpoint
    const result = await fetchPaginated(GET_CUSTOMERS, {
      limit: 5000, // Get enough for category counting
      offset: 0,
    })

    const catCounts = {}
    let total = 0
    let withPhone = 0
    let withEmail = 0
    let withAddress = 0

    ;(result.data || []).forEach((c) => {
      total++
      if (c.phone) withPhone++
      if (c.mail) withEmail++
      if (c.address || c.city) withAddress++

      const cat = c.category || "Uncategorized"
      catCounts[cat] = (catCounts[cat] || 0) + 1
    })

    return {
      categories: catCounts,
      stats: { total, withPhone, withEmail, withAddress, uniqueCategories: Object.keys(catCounts).length },
    }
  } catch (error) {
    console.error("Error fetching category counts:", error)
    return { categories: {}, stats: { total: 0, withPhone: 0, withEmail: 0, withAddress: 0, uniqueCategories: 0 } }
  }
}

const MuiCustomersList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  document.title = t("Customers") + " | FlexFlow ERP"

  // UI state
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false)

  // Category data (fetched once for chips and charts)
  const [categoryData, setCategoryData] = useState({ categories: {}, stats: {} })
  const [categoryLoading, setCategoryLoading] = useState(true)

  // ============================================================================
  // SERVER-SIDE PAGINATED DATA
  // ============================================================================
  const {
    data: customers,
    total,
    loading,
    error,
    pagination,
    searchTerm,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleFilterChange,
    refresh,
  } = usePaginatedData(getCustomersPaginated, {
    initialPageSize: 25,
    initialFilters: { categoryFilter: null },
    debounceMs: 300,
    initialSort: { field: "name", direction: "ASC" },
  })

  // ============================================================================
  // CATEGORY DATA FETCHING (for chips and analytics)
  // ============================================================================
  const fetchCategoryData = useCallback(async () => {
    setCategoryLoading(true)
    try {
      const data = await fetchCategoryCounts()
      setCategoryData(data)
    } catch (err) {
      console.error("Error fetching categories:", err)
    }
    setCategoryLoading(false)
  }, [])

  // Fetch category data on mount and location change
  useEffect(() => {
    fetchCategoryData()
  }, [location.key, fetchCategoryData])

  // Handle auth errors
  useEffect(() => {
    if (error && (error.includes("401") || error.includes("403"))) {
      localStorage.removeItem("authUser")
      navigate("/login")
    }
  }, [error, navigate])

  // User redirect logic (preserve existing behavior)
  useEffect(() => {
    const authUser = localStorage.getItem("authUser")
    const parsedAuthUser = authUser ? JSON.parse(authUser) : null
    if (parsedAuthUser) {
      const redirectMapping = {
        estiash: "/single/ΕΣΤΙΑΣΗ",
        kormakia: "/single/ΚΟΡΜΑΚΙΑ",
        fitilia: "/single/ΦΥΤΙΛΙΑ",
        pressa: "/single/ΠΡΕΣΣΑ",
        plyntirio: "/single/ΠΛΥΝΤΗΡΙΟ",
        trima: "/single/ΤΡΥΜΑ",
        tripsimo: "/single/ΤΡΙΨΙΜΟ",
        benetiko: "/single/ΒΕΝΕΤΙΚΟ",
        bafh: "/single/ΒΑΦΗ",
        xyta: "/single/ΧΥΤΑ",
        extruder: "/single/EXTRUDER",
      }
      const redirectPath = redirectMapping[parsedAuthUser.first_name]
      if (redirectPath) {
        navigate(redirectPath)
      }
    }
  }, [navigate])

  // ============================================================================
  // CATEGORY FILTER HANDLER
  // ============================================================================
  const handleCategoryFilterChange = useCallback(
    (category) => {
      setCategoryFilter(category)
      // Update paginated data filter
      // "all" means no category filter
      const filter = category === "all" ? null : `category=${category}`
      handleFilterChange({ categoryFilter: filter })
    },
    [handleFilterChange]
  )

  // ============================================================================
  // COMPUTED DATA
  // ============================================================================

  // Categories list for chips
  const categories = useMemo(() => {
    return Object.keys(categoryData.categories).filter((c) => c !== "Uncategorized").sort()
  }, [categoryData.categories])

  // Stats from pre-fetched data
  const stats = useMemo(() => categoryData.stats, [categoryData])

  // Chart data: Customers by category
  const categoryChartData = useMemo(() => {
    const colors = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#ec4899", "#14b8a6"]
    return Object.entries(categoryData.categories)
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
  }, [categoryData.categories])

  // Chart data: Customers by city (from loaded page - approximate)
  const cityChartData = useMemo(() => {
    const cityCounts = {}
    customers.forEach((c) => {
      const city = c.city || t("Unknown")
      cityCounts[city] = (cityCounts[city] || 0) + 1
    })
    const colors = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#6366f1"]
    return Object.entries(cityCounts)
      .map(([name, count], i) => ({ name, count, color: colors[i % colors.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6)
  }, [customers, t])

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleRowClick = useCallback(
    (customer) => {
      navigate(`/customer-detail/${customer.id}`)
    },
    [navigate]
  )

  const handleRefresh = useCallback(() => {
    refresh()
    fetchCategoryData()
  }, [refresh, fetchCategoryData])

  const handleClearSearch = useCallback(() => {
    handleSearchChange("")
  }, [handleSearchChange])

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
              <PeopleIcon sx={{ fontSize: 16, mr: 0.5, color: "#3b82f6" }} />
              {t("Customers")}
            </Typography>
          </Breadcrumbs>
          <Chip
            label={`${pagination.total.toLocaleString()} ${t("total")}`}
            size="small"
            sx={{ ml: 1, height: 22, fontSize: "0.688rem", bgcolor: "#f3f4f6", color: "#6b7280" }}
          />
        </Stack>

        <IconButton size="small" onClick={handleRefresh} disabled={loading} sx={{ color: "#6b7280", width: 32, height: 32 }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Expandable Analytics Panel */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          sx={{
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f9fafb" : "#fff",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}>
              {t("Analytics")}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: 2 }}>
              {stats.total || 0} {t("customers")} · {stats.uniqueCategories || 0} {t("categories")}
            </Typography>
            {categoryLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {analyticsExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#6b7280" }} />
            )}
          </IconButton>
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            {/* Stats Summary */}
            <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PeopleIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Total")}: <strong style={{ color: "#111827" }}>{stats.total || 0}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon sx={{ fontSize: 16, color: "#10b981" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Phone")}: <strong style={{ color: "#111827" }}>{stats.withPhone || 0}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Email")}: <strong style={{ color: "#111827" }}>{stats.withEmail || 0}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationOnIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("With Address")}: <strong style={{ color: "#111827" }}>{stats.withAddress || 0}</strong>
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
                <CategoryIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>
                  {t("Categories")}: <strong style={{ color: "#111827" }}>{stats.uniqueCategories || 0}</strong>
                </Typography>
              </Stack>
            </Stack>

            {/* Charts */}
            <Grid container spacing={2}>
              {/* Customers by Category Chart */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Customers by Category")}
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryChartData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#6b7280" }} width={75} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                        formatter={(value) => [value, t("Customers")]}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {categoryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              {/* Customers by City Chart (from current page) */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Customers by City")} ({t("current page")})
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cityChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #e5e7eb" }}
                        formatter={(value) => [value, t("Customers")]}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {cityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Search and Category Filters */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder={t("Search by name, label, VAT, phone, email...")}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": {
                height: 32,
                fontSize: "0.813rem",
                bgcolor: "#f9fafb",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${t("All")} (${stats.total || 0})`}
              size="small"
              onClick={() => handleCategoryFilterChange("all")}
              sx={{
                height: 26,
                fontSize: "0.7rem",
                fontWeight: 500,
                bgcolor: categoryFilter === "all" ? "#3b82f6" : "#f3f4f6",
                color: categoryFilter === "all" ? "#fff" : "#374151",
                "&:hover": { bgcolor: categoryFilter === "all" ? "#2563eb" : "#e5e7eb" },
              }}
            />
            {categories.slice(0, 5).map((cat) => {
              const count = categoryData.categories[cat] || 0
              return (
                <Chip
                  key={cat}
                  label={`${cat} (${count})`}
                  size="small"
                  onClick={() => handleCategoryFilterChange(cat)}
                  sx={{
                    height: 26,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    bgcolor: categoryFilter === cat ? "#3b82f6" : "#f3f4f6",
                    color: categoryFilter === cat ? "#fff" : "#374151",
                    "&:hover": { bgcolor: categoryFilter === cat ? "#2563eb" : "#e5e7eb" },
                  }}
                />
              )
            })}
            {categories.length > 5 && (
              <Chip
                label={`+${categories.length - 5} ${t("more")}`}
                size="small"
                sx={{ height: 26, fontSize: "0.7rem", bgcolor: "#f3f4f6", color: "#6b7280" }}
              />
            )}
          </Stack>
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", ml: "auto" }}>
            {pagination.startIndex}-{pagination.endIndex} {t("of")} {pagination.total} {t("results")}
          </Typography>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {loading && customers.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Customer")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Label")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("VAT")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Phone")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Email")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("Category")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1.25 }}>
                      {t("City")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.length > 0 ? (
                    customers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        hover
                        onClick={() => handleRowClick(customer)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } }}
                      >
                        <TableCell sx={{ py: 1.25 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: "#dbeafe", color: "#3b82f6", fontSize: "0.75rem" }}>
                              {(customer.name || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
                              {customer.name || "-"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          {customer.label ? (
                            <Chip
                              label={customer.label}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                bgcolor: "#fef3c7",
                                color: "#b45309",
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", fontFamily: "monospace", color: "#374151" }}>
                            {customer.vat || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {customer.phone || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {customer.mail || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          {customer.category ? (
                            <Chip
                              label={customer.category}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                bgcolor: "#eff6ff",
                                color: "#3b82f6",
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography sx={{ fontSize: "0.813rem", color: "#374151" }}>
                            {customer.city || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                          {searchTerm || categoryFilter !== "all" ? t("No customers found matching your criteria") : t("No customers found")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={(e, newPage) => handlePageChange(newPage)}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t("Rows")}
              sx={{
                borderTop: "1px solid #e5e7eb",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.75rem",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Error display */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 1.5,
            borderRadius: 1,
            border: "1px solid #fecaca",
            bgcolor: "#fef2f2",
          }}
        >
          <Typography color="error" variant="body2">
            {t("Error loading customers")}: {error}
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default MuiCustomersList
